import axios from "axios";
import { baseURL } from "../Constants";
import { Match } from "../models/Match";
import { MapStats } from "../models/MapStats";
import { UpcomingMatch } from "../models/UpcomingMatch";
import { BracketData } from "../models/BracketData";
import { Standing } from "../models/Standing";

const url = `${baseURL}/Match`;

export const getUpcomingMatchById = async (id: string): Promise<UpcomingMatch> => {
    try {
        const response = await axios.get(`${url}/getUpcomingMatchById/${id}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getMatchesBySeason = async (season: number): Promise<Match[]> => {
    try {
        const response = await axios.get(`${url}/getMatchesBySeason/${season}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getBracketData = async (season: number): Promise<BracketData> => {
    try {
        const response = await axios.get(`${url}/getBracketData?season=${season}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getGroupStandings = async (season: number): Promise<{ [key: string]: Standing[] }> => {
    try {
        const response = await axios.get(`${url}/getGroupStandings?season=${season}`);
        return response.data;
    } catch (error) {
        throw new Error(`Error fetching group standings: ${error}`);
    }
};

export const getLatestMatches = async (page: number): Promise<Match[]> => {
    try {
        const response = await axios.get(`${url}/latest?page=${page}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const updateMatch = async (match: Match): Promise<Match> => {
    try {
        const response = await axios.post(`${url}/updateMatch`, match);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getLatestSeason = async (): Promise<number> => {
    try {
        const response = await axios.get(`${url}/latestSeason`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getUpcomingMatches = async (): Promise<UpcomingMatch[]> => {
    try {
        const response = await axios.get(`${url}/getUpcomingMatches`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const createUpcomingMatch = async (match: UpcomingMatch): Promise<UpcomingMatch> => {
    try {
        const response = await axios.post(`${url}/createUpcomingMatch`, match);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const updateUpcomingMatch = async (match: UpcomingMatch): Promise<UpcomingMatch> => {
    try {
        const response = await axios.post(`${url}/updateUpcomingMatch`, match);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const deleteUpcomingMatch = async (matchId: string): Promise<boolean> => {
    try {
        const response = await axios.post(`${url}/deleteUpcomingMatch?matchId=${matchId}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getUpcomingMatchesForTeam = async (teamId: string): Promise<UpcomingMatch[]> => {
    try {
        const response = await axios.get(`${url}/getUpcomingMatchesForTeam?teamId=${teamId}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getOldestSeason = async (): Promise<number> => {
    try {
        const response = await axios.get(`${url}/oldestSeason`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getMatchesForTeamBySeason = async (teamId: string, season: number): Promise<Match[]> => {
    try {
        const response = await axios.get(`${url}/team`, { params: { teamId, season } });
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getVetoStatsForTeamBySeason = async (teamId: string, season: string): Promise<MapStats[]> => {
    try {
        const response = await axios.get(`${url}/getVetoStats`, { params: { teamid: teamId, season } });
        return response.data.map((item: { mapName: string; amountPicked: number; amountBanned: number; amountPlayed: number; amountWon: number; chosenCTPercentage: number; }) => new MapStats(
            item.mapName,
            item.amountPicked,
            item.amountBanned,
            item.amountPlayed,
            item.amountWon,
            item.chosenCTPercentage,
        ));
    }
    catch (error) {
        throw error;
    }
}

export const getMatchesForPlayerBySeason = async (steamId: string, season: number): Promise<Match[]> => {
    try {
        const response = await axios.get(`${url}/player`, { params: { steamId, season } });
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getMatchById = async (matchId: string): Promise<Match[]> => {
    try {
        const response = await axios.get(`${url}/${matchId}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getDivisionStandings = async (season: number, division: string) => {
    try {
        const response = await axios.get(`${url}/getAllDivisionStandings?season=${season}&majorDivision=${division}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getDivisionsBySeason = async (season: number): Promise<string[]> => {
    try {
        const response = await axios.get(`${url}/getDivisionsBySeason?season=${season}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getPlayersAvgRating = async (matchId: string): Promise<{ [steamId: string]: number }> => {
    try {
        const response = await axios.get(`${url}/getPlayersAvgRating?matchId=${matchId}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}
